import type { MetricUnit } from './types';

const MINUS = '−'; // true minus sign (handoff §9)

/** $1,420,000 · negative as −$444,000 (never "$-444,000"). */
export function money(n: number): string {
  const sign = n < 0 ? MINUS : '';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
}

/** Thousands-separated integer. */
export function count(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Percent from a 0–1 ratio. No decimal unless the value is under 10%
 * (4.1%, 96%) — handoff §9.
 */
export function pct(ratio: number): string {
  const p = ratio * 100;
  return p < 10 ? `${p.toFixed(1)}%` : `${String(Math.round(p))}%`;
}

/** Hours to one decimal with a short unit: "5.1 h". */
export function hoursShort(n: number): string {
  return `${n.toFixed(1)} h`;
}

/** Hours for a headline: "5.1 hours". */
export function hoursLong(n: number): string {
  return `${n.toFixed(1)} hours`;
}

/** Format a metric value for a tile / inline figure by its unit. */
export function fmtMetric(value: number | null, unit: MetricUnit): string {
  if (value === null) return 'Not recorded';
  switch (unit) {
    case 'usd':
      return money(value);
    case 'pct':
      return pct(value);
    case 'days':
      return `${count(value)} days`;
    case 'hours':
      return hoursLong(value);
    case 'count':
    default:
      return count(value);
  }
}

/** Nicely formats a backup timestamp like "Apr 30, 2026, 11:40 PM". */
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Apr 30, 2026" from an ISO date. */
export function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
